import parse from 'html-react-parser'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import { CustomTooltip } from '@components/CustomTooltip'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoCustomListBulkEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoCustomListBulkEventType, PendoEventType } from '@modules/pendo/types'
import MultiSelect from '@modules/search/components/FiltersSection/MultiSelect'
import { AnyProfile, CompanyAsset, HistoryProfileEvent } from '@modules/search/types'
import shortlistApi from '@modules/shortlist/api'
import { useShortlistMap } from '@modules/shortlist/hooks/useShortlistMap'
import { Shortlist } from '@modules/shortlist/types'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import GradingIcon from '@mui/icons-material/Grading'
import { Link } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import useSelection from '@shared/hooks/useSelection'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import AddNewShortlistSection from '../AddNewShortlistSection'
import {
  AddIconCircle,
  AddIconWrapper,
  AddNewShortlistFooterContainer,
  EmptyProfileItemRoundButton,
  LineDivider,
  PopoverInner,
  ProfileItemRoundButton,
  SelectedShortlistTitle,
  ShortlistItemsContainer,
  StyledAddIcon,
  StyledArrowOutwardIcon,
  StyledShortlistIcon,
} from './index.styles'

interface ShortlistProfileButtonProps {
  profile: AnyProfile
  events?: HistoryProfileEvent[]
  location?: string
}

const ShortlistProfileButton = (props: ShortlistProfileButtonProps) => {
  const { profile, events, location = 'ShortlistProfileButton' } = props
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const containerRef = useRef<Nullable<HTMLDivElement>>(null)
  const scrollRef = useRef<Nullable<HTMLDivElement>>(null)
  const [hasScrollbar, setHasScrollbar] = useState(false)
  const [isConfirmExitPopupShown, setIsConfirmExitPopupShown] = useState(false)
  const [popoverAnchor, setPopoverAnchorEl] = useState<Nullable<HTMLDivElement>>(null)
  const [isAddNewShortlistVisible, setIsAddNewShortlistVisible] = useState(false)
  const [newShortlistName, setNewShortlistName] = useState('')
  const { hasAccessToMultipleShortlists, hasAccessToShortlists } = useFeatureFlags()
  const { canAddShortlists, map: shortlistProfileMap, shortlists, isUniqueName, getKey } = useShortlistMap()
  const { user } = useAuthContext()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { isSmallMobile } = useScreenDimensions()

  useEffect(() => {
    if (scrollRef.current) {
      const isScrollbarVisible = scrollRef.current.scrollHeight > scrollRef.current.clientHeight
      setHasScrollbar(isScrollbarVisible)
    }
  }, [scrollRef])

  const alreadySelectedShortlists = useMemo(() => {
    const duplicates = (profile as CompanyAsset)?.duplicate_ids ?? []

    if (duplicates?.length > 1) {
      return duplicates.flatMap(id => shortlistProfileMap.get(getKey({ ...profile, id }))).filter(Boolean) as Shortlist[]
    }

    return shortlistProfileMap.get(getKey(profile))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistProfileMap, profile])

  const alreadySelectedShortlistIds = useMemo(() => alreadySelectedShortlists?.map(i => i.id) ?? [], [alreadySelectedShortlists])
  const shortlistIds = useMemo(() => shortlists?.map(i => i.id) ?? [], [shortlists])

  const { mutate: updateShortlistProfiles } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfiles.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key] })
    },
  })

  const {
    toggleSelectItem: toggleShortlistItem,
    selectedIds: selectedShortlistIds,
    clearSelectedItems: clearSelectedShortlistIds,
    changedItems: newlyUpdatedShortlists,
  } = useSelection(shortlistIds, {
    trackChanges: true,
    initiallySelectedItems: alreadySelectedShortlistIds,
    onItemSelected: shId => {
      const pendoEvent = preparePendoCustomListBulkEvent({
        user,
        location,
        customListId: shId,
        eventType: PendoCustomListBulkEventType.Tick,
        profileType: profile['@type'],
      })
      trackPendoEvent(PendoEventType.AddToCustomList, pendoEvent)

      updateShortlistProfiles({
        profiles: [
          {
            searchDocId: profile.id,
            profileType: profile['@type'],
            profileData: profile,
            events: events ?? [],
            duplicate_ids: (profile as CompanyAsset).duplicate_ids,
          },
        ],
        addToShortlistIds: [Number(shId)],
        removeFromShortlistIds: [],
      })
    },
    onItemUnselected: shId => {
      const pendoEvent = preparePendoCustomListBulkEvent({
        user,
        location,
        customListId: shId,
        eventType: PendoCustomListBulkEventType.Untick,
        profileType: profile['@type'],
      })
      trackPendoEvent(PendoEventType.AddToCustomList, pendoEvent)
      updateShortlistProfiles({
        profiles: [
          {
            searchDocId: profile.id,
            profileType: profile['@type'],
            profileData: profile,
            events: events ?? [],
            duplicate_ids: (profile as CompanyAsset).duplicate_ids,
          },
        ],
        addToShortlistIds: [],
        removeFromShortlistIds: [Number(shId)],
      })
    },
  })

  const shortlistOptions = useMemo(() => shortlists.map(i => ({ label: i.name, value: i.id })), [shortlists])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (containerRef.current) {
      setPopoverAnchorEl(containerRef.current)
    }
  }

  const handleClose = (shouldShowConfirmationPopup: boolean) => {
    if (
      !isConfirmExitPopupShown &&
      shouldShowConfirmationPopup &&
      (Object.keys(newlyUpdatedShortlists).length || newShortlistName.length)
    ) {
      close()
      return true
    }

    close()
    return true
  }

  const handleCloseAddNewShortlistSection = () => {
    const pendoEvent = preparePendoCustomListBulkEvent({
      user,
      location,
      eventType: PendoCustomListBulkEventType.NewCancel,
      profileType: profile['@type'],
    })
    trackPendoEvent(PendoEventType.AddToCustomList, pendoEvent)
    setIsAddNewShortlistVisible(false)

    if (!shortlists.length) {
      handleClose(false)
    }

    return true
  }
  const close = () => {
    setPopoverAnchorEl(null)
    setIsAddNewShortlistVisible(false)

    setTimeout(() => {
      clearSelectedShortlistIds()
      setNewShortlistName('')
    }, 500)
  }

  const onCreateShortlistSuccess = (createdShortlist?: Shortlist) => {
    const pendoEvent = preparePendoCustomListBulkEvent({
      user,
      location,
      eventType: PendoCustomListBulkEventType.NewAdd,
      profileType: profile['@type'],
    })
    trackPendoEvent(PendoEventType.AddToCustomList, pendoEvent)
    if (createdShortlist) {
      toggleShortlistItem(createdShortlist.id)
    }
  }

  const shortlistsTooltipContent = useMemo(() => {
    const maxElementsToShow = 5
    const lastElement =
      (alreadySelectedShortlists?.length === maxElementsToShow && (
        <Link
          key={alreadySelectedShortlists[maxElementsToShow - 1].id}
          href={`/plus/custom-list?id=${alreadySelectedShortlists[maxElementsToShow - 1].id}`}
        >
          <SelectedShortlistTitle>
            <strong>{alreadySelectedShortlists[maxElementsToShow - 1].name}</strong>
            <StyledArrowOutwardIcon />
          </SelectedShortlistTitle>
        </Link>
      )) ||
      ((alreadySelectedShortlists?.length as number) > maxElementsToShow && <div>...</div>) ||
      null

    return (
      <Box maxWidth="120px" paddingRight="12px">
        {alreadySelectedShortlists?.slice(0, maxElementsToShow - 1)?.map(sh => (
          <Link key={sh.id} href={`/plus/custom-list?id=${sh.id}`}>
            <SelectedShortlistTitle>
              <strong>{sh.name}</strong>
              <StyledArrowOutwardIcon />
            </SelectedShortlistTitle>
          </Link>
        ))}
        <SelectedShortlistTitle>{lastElement}</SelectedShortlistTitle>
      </Box>
    )
  }, [alreadySelectedShortlists])

  const ActiveButton = useMemo(
    () =>
      isSmallMobile ? (
        <IconButton id="shorlist-manage_profile_button" onClick={handleClick}>
          <AddIconWrapper sx={{ padding: '5px' }}>
            <GradingIcon htmlColor="#fff" fontSize="small" />
          </AddIconWrapper>
        </IconButton>
      ) : (
        <CustomTooltip title={shortlistsTooltipContent}>
          <ProfileItemRoundButton
            data-testid={`in_a_shortlist_btn_${profile.id}`}
            variant="contained"
            id="shorlist-manage_profile_button"
            onClick={handleClick}
            endIcon={<StyledShortlistIcon />}
          >
            {parse(t(translationKeys.IN_CUSTOM_LIST))}
          </ProfileItemRoundButton>
        </CustomTooltip>
      ),
    [isSmallMobile, shortlistsTooltipContent, profile.id, t],
  )

  const EmptyButton = isSmallMobile ? (
    <IconButton id="shorlist-add_profile_button" onClick={handleClick}>
      <AddIconWrapper>
        <StyledAddIcon />
      </AddIconWrapper>
    </IconButton>
  ) : (
    <EmptyProfileItemRoundButton
      data-testid={`add_to_shortlist_btn_${profile.id}`}
      id="shorlist-add_profile_button"
      variant="outlined"
      onClick={handleClick}
      endIcon={
        <AddIconWrapper>
          <StyledAddIcon />
        </AddIconWrapper>
      }
    >
      {t(translationKeys.CUSTOM_LIST_BTN)}
    </EmptyProfileItemRoundButton>
  )

  return (
    <>
      {hasAccessToShortlists && (shortlists?.length || canAddShortlists) && (
        <Box ref={containerRef}>{alreadySelectedShortlistIds?.length ? ActiveButton : EmptyButton}</Box>
      )}

      <Popover
        id={popoverAnchor ? 'shortlist_popover' : undefined}
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={() => handleClose(true)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          marginTop: '10px',
        }}
      >
        <PopoverInner>
          {!!shortlists?.length && (
            <>
              <Box position="absolute" top="2px" right="2px">
                <IconButton onClick={() => handleClose(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box mb="10px">
                <Typography variant="h2">{t(translationKeys.ADD_PROSPECT_TO_CUSTOM_LIST)}</Typography>
              </Box>
              <ShortlistItemsContainer ref={scrollRef} $withScrollBar={!!hasScrollbar}>
                <MultiSelect values={selectedShortlistIds} label="" options={shortlistOptions} onChange={toggleShortlistItem} />
              </ShortlistItemsContainer>

              {hasAccessToMultipleShortlists && (
                <>
                  <LineDivider />
                  {!isAddNewShortlistVisible && (
                    <AddNewShortlistFooterContainer onClick={() => setIsAddNewShortlistVisible(true)}>
                      <AddIconCircle>
                        <AddIcon color="primary" fontSize="medium" />
                      </AddIconCircle>
                      <Box ml="10px">
                        <Typography variant="body1">{t(translationKeys.ADD_YOUR_CUSTOM_LIST)}</Typography>
                      </Box>
                    </AddNewShortlistFooterContainer>
                  )}
                </>
              )}
            </>
          )}
          {(isAddNewShortlistVisible || !shortlists?.length) && (
            <AddNewShortlistSection
              isUniqueName={isUniqueName}
              shortlists={shortlists}
              onSuccess={onCreateShortlistSuccess}
              onChange={setNewShortlistName}
              onCancel={handleCloseAddNewShortlistSection}
            />
          )}
        </PopoverInner>
      </Popover>
      {isConfirmExitPopupShown && (
        <ConfirmPopup
          id="shortlist-profile-button-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen={isConfirmExitPopupShown}
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setIsConfirmExitPopupShown(false)}
          onConfirm={() => {
            close()
            setIsConfirmExitPopupShown(false)
          }}
        />
      )}
    </>
  )
}

export default ShortlistProfileButton
